'use client';

import { useEffect, useMemo } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { AppointmentCardQuery } from './appointment-card-query.component';
import { AppointmentsEmptyState } from './appointments-empty-state.component';
import { AppointmentsLoading } from './appointments-loading.component';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs.component';
import { Button } from '@/components/ui/button/button.component';
import { AppointmentsLifecycleEnum } from '@/types/enums/appointments.enum';
import { Appointment } from '@/types/appointments.types';
import { useFetchAppointmentsList } from '@/hooks/fetch/appointment/use-fetch-appointments-list.hook';
import { AppointmentsTabsEnum, useAppointmentsTabs } from '@/hooks/fetch/appointment/use-appointment-tab.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { MyAppointmentsTabAba } from '@/types/enums/analytics/my-appointments/my-appointments-analytics.enum';

const TAB_TEST_IDS: Record<AppointmentsTabsEnum, string> = {
  [AppointmentsTabsEnum.FUTURE]: DataTestIdEnum.APPOINTMENTS_TAB_CONTENT_FUTURE,
  [AppointmentsTabsEnum.PAST]: DataTestIdEnum.APPOINTMENTS_TAB_CONTENT_PAST,
  [AppointmentsTabsEnum.OTHER]: DataTestIdEnum.APPOINTMENTS_TAB_CONTENT_OTHER,
};

const TAB_TRIGGER_TEST_IDS: Record<AppointmentsTabsEnum, string> = {
  [AppointmentsTabsEnum.FUTURE]: DataTestIdEnum.APPOINTMENTS_TAB_TRIGGER_FUTURE,
  [AppointmentsTabsEnum.PAST]: DataTestIdEnum.APPOINTMENTS_TAB_TRIGGER_PAST,
  [AppointmentsTabsEnum.OTHER]: DataTestIdEnum.APPOINTMENTS_TAB_TRIGGER_OTHER,
};

const TAB_ABA: Record<AppointmentsTabsEnum, MyAppointmentsTabAba> = {
  [AppointmentsTabsEnum.FUTURE]: MyAppointmentsTabAba.PROXIMOS,
  [AppointmentsTabsEnum.PAST]: MyAppointmentsTabAba.HISTORICO,
  [AppointmentsTabsEnum.OTHER]: MyAppointmentsTabAba.PENDENTES,
};

export function AppointmentsTabs() {
  const futureAppointments = useFetchAppointmentsList({
    lifecycle: AppointmentsLifecycleEnum.FUTURE,
  });
  const pastAppointments = useFetchAppointmentsList({
    lifecycle: AppointmentsLifecycleEnum.PAST,
  });
  const otherAppointments = useFetchAppointmentsList({
    lifecycle: AppointmentsLifecycleEnum.OTHER,
  });

  const { tabs, activeTab, onTabSelect } = useAppointmentsTabs({
    hasFutureAppointments: Number(futureAppointments.listSize) > 0,
    hasPastAppointments: Number(pastAppointments.listSize) > 0,
    hasOtherAppointments: Number(otherAppointments.listSize) > 0,
  });

  const dictionary = useMemo(
    () => ({
      [AppointmentsTabsEnum.FUTURE]: futureAppointments,
      [AppointmentsTabsEnum.PAST]: pastAppointments,
      [AppointmentsTabsEnum.OTHER]: otherAppointments,
    }),
    [futureAppointments, pastAppointments, otherAppointments],
  );

  const analytics = getAnalyticsPlugin();

  useEffect(() => {
    if (!activeTab) {
      return;
    }
    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_PAGE_VIEWED, { aba: TAB_ABA[activeTab] });
  }, [activeTab, analytics]);

  const hasFetched
    = futureAppointments.listSize !== undefined
      && pastAppointments.listSize !== undefined
      && otherAppointments.listSize !== undefined;

  if (!hasFetched) {
    return <AppointmentsLoading />;
  }

  if (tabs.length === 0) {
    return <AppointmentsEmptyState />;
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={onTabSelect}
      activationMode="manual"
      className="w-full flex flex-col items-center"
      data-testid={DataTestIdEnum.APPOINTMENTS_TABS}
    >
      <TabsList className="flex flex-col items-center gap-4 mb-4">
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.key}
            value={tab.key}
            data-testid={TAB_TRIGGER_TEST_IDS[tab.key]}
          >
            {tab.title}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => {
        const { list, fetchAppointmentById, hasFetchMore, isLoading, fetchNextPage, onAppointmentUpdate } = dictionary[tab.key];
        return (
          <TabsContent
            key={tab.key}
            value={tab.key}
            className="w-full flex flex-col items-center gap-4"
            data-testid={TAB_TEST_IDS[tab.key]}
          >
            {list?.map(
              ({ failed, ...item }) =>
                !failed && (
                  <AppointmentCardQuery
                    key={`key-tab-card-${tab.key}-${item.id}`}
                    appointment={item}
                    lifecycle={tab.queryKey}
                    fetchAppointmentById={() => fetchAppointmentById(item?.id)}
                    onAppointmentUpdate={(appointment: Partial<Appointment>) => {
                      if (item?.id) {
                        onAppointmentUpdate(item.id, appointment);
                      }
                    }}
                  />
                ),
            )}
            {hasFetchMore && (
              <Button
                variant="secondary"
                data-testid={DataTestIdEnum.APPOINTMENTS_LOAD_MORE}
                className="mx-auto w-full md:max-w-3xl"
                onClick={fetchNextPage}
                isLoading={isLoading}
              >
                Carregar mais
              </Button>
            )}
          </TabsContent>
        );
      })}
    </Tabs>
  );
}
